import Button, { IconButton } from '@atlaskit/button/new';

import CrossIcon from '@atlaskit/icon/core/cross';

import { Box, Inline, Text } from '@atlaskit/primitives/compiled';

import { cssMap } from '@atlaskit/css';

import { token } from '@atlaskit/tokens';

import { type MessageDescriptor, useIntl } from 'react-intl';

import { ThirdPartyIcons } from '@post-office/browser--rovo-expansion/common/index';

import { type BannerVariant } from '../index';

import { messages, variantMessages } from '../messages';

import {
	type ConnectorProps,
	ConnectorSources,
	OAuthSource,
	type OAuthSource as OAuthSourceType,
} from '@post-office/browser--rovo-expansion/search-page-results-quick-find';

import { type JSX } from 'react/jsx-runtime';

const styles = cssMap({
	container: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.large'),
	},
});

export type SearchPageQuickFind3PBannerContext = {
	productSource: string;
	variant: BannerVariant;
	onAuthorize: (oAuthSource: OAuthSourceType) => void;
	connector: ConnectorProps;
	teamConnectAppPercentage?: number;
};

const getVariantMessages = (
	mode: 'smartlinks' | 'full-connector',
	variant: BannerVariant,
): { heading: MessageDescriptor; body?: MessageDescriptor } => {
	if (mode === 'smartlinks') {
		return variantMessages.smartlinks[variant as keyof typeof variantMessages.smartlinks];
	}
	return variantMessages['full-connector'][
		variant as keyof (typeof variantMessages)['full-connector']
	];
};

export const SearchPageQuickFind3PBanner = ({
	context,
	onDismiss,
}: {
	context: SearchPageQuickFind3PBannerContext;
	onDismiss: (args?: { reason?: string }) => void;
}): JSX.Element => {
	const { connector, variant, teamConnectAppPercentage, onAuthorize } = context;
	const { connectorName, connectorMode } = connector;
	const { formatMessage } = useIntl();

	const connectorNameToUse =
		connector.connectorProductKey === 'sharepoint' ? 'SharePoint' : connectorName;

	const connectorModeToUse =
		connectorMode === ConnectorSources.Smartlinks
			? ConnectorSources.Smartlinks
			: ConnectorSources.Full;
	const variantMsgs = getVariantMessages(connectorModeToUse, variant);
	const headingText = formatMessage(variantMsgs.heading, {
		connectorName: connectorNameToUse,
		teamConnectAppPercentage,
	});
	const bodyText = variantMsgs.body
		? formatMessage(variantMsgs.body, {
				connectorName: connectorNameToUse,
				teamConnectAppPercentage,
			})
		: undefined;
	const connectText = formatMessage(messages.connect, { connectorName: connectorNameToUse });
	const dismissLabel = formatMessage(messages.dismiss);

	const ConnectorIcon = ThirdPartyIcons.get(connector.connectorProductKey);

	const handleDismiss = (reason: string) => onDismiss({ reason });

	return (
		<Box
			aria-labelledby={`rovo-search-page-quick-find-3p-${connectorModeToUse}-${variant}-banner`}
			backgroundColor="color.background.accent.blue.subtlest"
			xcss={styles.container}
			testId="rovo-search-page-quick-find-3p-connector-pre-auth-nudge"
		>
			<Inline alignBlock="center" spread="space-between">
				<Inline alignBlock="center" space="space.100">
					{ConnectorIcon && <ConnectorIcon size="small" label={connectorNameToUse} />}
					<Text id={`rovo-search-page-quick-find-3p-${connectorModeToUse}-${variant}-heading`}>
						<Text weight="semibold">{headingText}</Text>
						{bodyText && ` ${bodyText}`}
					</Text>
				</Inline>
				<Inline alignBlock="center" space="space.100">
					{/* eslint-disable-next-line @post-office/message-linting/require-instrumented-interactions */}
					<Button
						appearance="primary"
						onClick={() => onAuthorize(OAuthSource.quickFindOAuthBanner)}
						testId="rovo-search-page-quick-find-3p-connect"
					>
						{connectText}
					</Button>
					{/* eslint-disable-next-line @post-office/message-linting/require-instrumented-interactions */}
					<IconButton
						appearance="subtle"
						icon={CrossIcon}
						label={dismissLabel}
						onClick={() => handleDismiss('dismiss-button')}
						testId="rovo-search-page-quick-find-3p-dismiss"
					/>
				</Inline>
			</Inline>
		</Box>
	);
};
